import React from "react";
import DiamondDivider from "../../theme/DiamondDivider";

const ARABIC_DIACRITICS_REGEX =
  /[ؐ-ًؚ-ٰٟۖ-ۭ]/g;
const PUNCTUATION_REGEX = /[.,!?؟،؛:"«»()[\]]/g;

// Mirror of TopicDetail's normalizeArabic — harakat + punctuation stripped.
function normalizeArabic(str) {
  if (!str) return "";
  return str
    .replace(ARABIC_DIACRITICS_REGEX, "")
    .replace(PUNCTUATION_REGEX, "")
    .trim();
}

function PlayButton({ onClick, loading, playing, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="p-1 rounded-full shrink-0 transition-colors hover:bg-dt-forest-soft"
      style={{ color: playing ? "var(--gold-bright)" : "var(--parchment-dim)" }}
    >
      {loading ? (
        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
          <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      ) : playing ? (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <rect x="5" y="5" width="14" height="14" rx="2" />
        </svg>
      ) : (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M6 4l14 8-14 8V4z" />
        </svg>
      )}
    </button>
  );
}

/**
 * PublicScenario — the dialogue for the shared-topic page.
 *
 * Slim re-implementation of TopicDetail's scenario renderer: RTL Amiri token
 * rows where words matching the topic's word list are tappable (→ bubble),
 * plus transliteration/translation lines. Uses the same CSS classes
 * (tok-word, tok-selected, font-amiri) so it looks identical to the app.
 * No grammar modes, no admin controls, no per-user coloring.
 *
 * Props:
 *   turns, words         — from the public topic payload
 *   tts                  — usePublicTTS instance (per-turn play; hidden when showTurnPlay=false)
 *   onTokenClick(word, rect, turn, tokenIdx)
 *   selectedWordId       — highlights every token of the selected word
 *   showTurnPlay         — per-line TTS play buttons (off for YouTube topics)
 *   activeTurnId, activeWordIndex — synced-video highlight (Listening step)
 *   videoControls        — { isVideoPlaying, activeTurnId, onToggle(turn) };
 *                          shows the per-line video play/pause button (red,
 *                          like the app) on turns carrying video_start_ms
 *   sectionRef           — ref to the scroll container, so the host can
 *                          auto-scroll the transcript with video playback
 *   maxHeightClass       — scroll container sizing per step layout
 */
export default function PublicScenario({
  turns,
  words,
  tts,
  onTokenClick,
  selectedWordId,
  showTurnPlay = true,
  activeTurnId = null,
  activeWordIndex = null,
  videoControls = null,
  sectionRef = null,
  maxHeightClass = "",
}) {
  const findWordForToken = (token) => {
    const normToken = normalizeArabic(token);
    if (!normToken) return null;
    return words.find((w) => normalizeArabic(w.text) === normToken) || null;
  };

  const renderToken = (turn, tok, idx) => {
    const match = findWordForToken(tok);
    const isActiveWord = activeTurnId === turn.id && idx === activeWordIndex;
    const activeWordCls = isActiveWord ? " bg-amber-300 text-ink-900 rounded shadow-sm" : "";

    if (!match) {
      return (
        <span key={idx} className={isActiveWord ? "px-1 py-0.5 transition-colors" + activeWordCls : undefined}>
          {tok}
        </span>
      );
    }

    let classes = "cursor-pointer px-2 py-0.5 rounded-lg tok-word";
    if (selectedWordId === match.id) classes += " tok-selected";
    if (isActiveWord) classes = "cursor-pointer px-2 py-0.5 rounded-lg" + activeWordCls;

    return (
      <button
        key={idx}
        type="button"
        className={classes}
        style={{ border: "none" }}
        onClick={(e) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          onTokenClick(match, rect, turn, idx);
        }}
      >
        {tok}
      </button>
    );
  };

  return (
    <section
      ref={sectionRef}
      style={{ background: "var(--ink)", borderColor: "var(--panel-line)" }}
      className={`dark-scope p-4 rounded-2xl border ${maxHeightClass ? `overflow-y-auto ${maxHeightClass} scroll-elegant` : "overflow-x-auto"}`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h3 className="text-sm font-cormorant font-semibold" style={{ color: "var(--parchment)" }}>
          Scenario / Dialogue
        </h3>
        {tts && (tts.playingTurnId || tts.loadingTurnId) && (
          <button
            type="button"
            onClick={tts.stop}
            className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border transition-colors"
            style={{ color: "var(--tag-rose)", background: "var(--tag-rose-bg)", borderColor: "transparent" }}
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
            Stop
          </button>
        )}
      </div>

      <div className="space-y-3">
        {turns.map((turn) => (
          <React.Fragment key={turn.id}>
            <DiamondDivider className="my-4" />
            <div
              ref={(el) => { if (tts) tts.turnRefsRef.current[turn.id] = el; }}
              className={`pb-2 transition-colors rounded ${
                activeTurnId === turn.id || tts?.playingTurnId === turn.id ? "-mx-1 px-1" : ""
              }`}
              style={
                activeTurnId === turn.id
                  ? { background: "var(--gold-bg)", boxShadow: "0 0 0 1px rgba(201,162,39,0.35)" }
                  : tts?.playingTurnId === turn.id
                  ? { background: "var(--forest-soft)" }
                  : {}
              }
            >
              {/* Speaker + play */}
              {(turn.speaker || (showTurnPlay && tts) || (videoControls && turn.video_start_ms != null)) && (
                <div className="flex items-center justify-end gap-1 mb-1">
                  {videoControls && turn.video_start_ms != null && (() => {
                    // This line is the one currently playing in the video.
                    const isActiveLine = videoControls.activeTurnId === turn.id;
                    const isThisPlaying = videoControls.isVideoPlaying && isActiveLine;
                    return (
                      <button
                        type="button"
                        onClick={() => videoControls.onToggle(turn)}
                        title={isThisPlaying ? "Pause video" : "Play the video from this line"}
                        aria-label={isThisPlaying ? "Pause video" : "Play video from this line"}
                        className="p-1 rounded text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                      >
                        {isThisPlaying ? (
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>
                    );
                  })()}
                  {showTurnPlay && tts && (
                    <PlayButton
                      onClick={() =>
                        tts.playingTurnId === turn.id || tts.loadingTurnId === turn.id
                          ? tts.stop()
                          : tts.playTurn(turn)
                      }
                      loading={tts.loadingTurnId === turn.id}
                      playing={tts.playingTurnId === turn.id}
                      title={tts.playingTurnId === turn.id ? "Stop" : "Play this line"}
                    />
                  )}
                  {turn.speaker && (
                    <span className="text-xs font-medium font-arabic" style={{ color: "var(--parchment-dim)" }} dir="rtl">
                      {turn.speaker}
                    </span>
                  )}
                </div>
              )}

              {/* Arabic line with clickable tokens (RTL) */}
              <div
                dir="rtl"
                className="flex flex-wrap gap-x-1 gap-y-2 justify-start mb-2 font-amiri text-2xl leading-relaxed"
                style={{ color: "var(--parchment)" }}
              >
                {(turn.arabic || "").split(/\s+/).map((tok, idx) => renderToken(turn, tok, idx))}
              </div>

              {turn.transliteration && (
                <div className="text-xs italic text-right" style={{ color: "var(--parchment-dim)" }} dir="ltr">
                  {turn.transliteration}
                </div>
              )}
              {turn.translation && (
                <div className="text-sm mt-0.5 text-right" style={{ color: "var(--parchment)" }} dir="ltr">
                  {turn.translation}
                </div>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}
